/**
 * The one daily-cap counter.
 *
 * A team's "games that day" is counted for THAT TEAM, never for a group of
 * teams pooled together. The grid used to count the union of both teams in the
 * game being dragged, so a game those two teams share was counted once for a
 * pair that plays three each, and the board printed five. Nobody plays five.
 *
 * Every screen that says "would play N games that day" — the grid's drag
 * feedback, the check dialog, the team summary and the publish check — must
 * agree, so the count and the comparison live here and nowhere else.
 */

export type CapGame = { teams: string[] };

export type CapRow = {
  /** The team's registration id. */
  team: string;
  /** Games that team plays on that date, including the proposed placement. */
  games: number;
  /** The limit it exceeds. */
  limit: number;
};

/** Is this many games on one day over the limit? A limit of 0 is "no limit". */
export const isOverDailyCap = (games: number, maxPerDay: number | null | undefined): boolean => {
  const cap = Number(maxPerDay ?? 0);
  return cap > 0 && games > cap;
};

/**
 * How many games each of `teams` plays among `board` — one count per team,
 * so a game two of them share counts once for each, never once in total.
 */
export function countByTeam(board: CapGame[], teams: string[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const t of teams) counts.set(t, 0);
  for (const g of board ?? []) {
    for (const t of g?.teams ?? []) {
      if (!counts.has(t)) continue;
      counts.set(t, (counts.get(t) ?? 0) + 1);
    }
  }
  return counts;
}

/**
 * Which of `teams` would be over the cap on a day, counted separately.
 * `adding` is the placement being considered (1 while dragging, 0 for a board
 * that is already as it will be).
 */
export function teamsOverDailyCap(opts: {
  board: CapGame[];
  teams: string[];
  maxPerDay: number | null | undefined;
  adding?: number;
}): CapRow[] {
  const cap = Number(opts.maxPerDay ?? 0);
  if (cap <= 0) return [];
  const add = opts.adding ?? 0;
  const counts = countByTeam(opts.board, opts.teams);
  const rows: CapRow[] = [];
  for (const team of opts.teams) {
    const games = (counts.get(team) ?? 0) + add;
    if (isOverDailyCap(games, cap)) rows.push({ team, games, limit: cap });
  }
  return rows;
}

/**
 * One sentence per team that is over. Two teams over the cap is two lines —
 * never one line with their totals added together.
 */
export function dailyCapLines(rows: CapRow[], nameOf: (id: string) => string): string[] {
  return rows.map((r) => `${nameOf(r.team)} would play ${r.games} games that day. The limit is ${r.limit}.`);
}
